import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/shared/store';
import type { EnterpriseDetail } from '@/features/enterprise/api/enterpriseApi';

type EnterpriseFilters = {
  searchInput: string;
  status: string;
};

type EnterpriseDetailsEditState = {
  originalDetails: EnterpriseDetail | null;
  editedDetails: (Partial<EnterpriseDetail> & Record<string, unknown>) | null;
  editingField: string | null;
};

type EnterpriseState = {
  filters: EnterpriseFilters;
  detailsEdit: EnterpriseDetailsEditState;
};

const initialState: EnterpriseState = {
  filters: {
    searchInput: '',
    status: '',
  },
  detailsEdit: {
    originalDetails: null,
    editedDetails: null,
    editingField: null,
  },
};

const enterpriseSlice = createSlice({
  name: 'enterprise',
  initialState,
  reducers: {
    setSearchInput(state, action: PayloadAction<string>) {
      state.filters.searchInput = action.payload;
    },
    setStatus(state, action: PayloadAction<string>) {
      state.filters.status = action.payload;
    },
    clearFilters(state) {
      state.filters.searchInput = '';
      state.filters.status = '';
    },
    // Enterprise details edit actions
    setOriginalDetails(state, action: PayloadAction<EnterpriseDetail>) {
      state.detailsEdit.originalDetails = action.payload;
      state.detailsEdit.editedDetails = { ...action.payload };
    },
    startEditingField(state, action: PayloadAction<string>) {
      state.detailsEdit.editingField = action.payload;
      if (!state.detailsEdit.editedDetails) {
        state.detailsEdit.editedDetails = {};
      }
    },
    stopEditingField(state) {
      state.detailsEdit.editingField = null;
    },
    updateEditedField(
      state,
      action: PayloadAction<{ fieldName: string; value: unknown }>
    ) {
      const { fieldName, value } = action.payload;
      if (!state.detailsEdit.editedDetails) {
        state.detailsEdit.editedDetails = {};
      }
      (state.detailsEdit.editedDetails as Record<string, unknown>)[fieldName] =
        value;
    },
    resetEditedDetails(state) {
      state.detailsEdit.editedDetails = null;
      state.detailsEdit.editingField = null;
    },
    discardChanges(state) {
      state.detailsEdit.editedDetails = null;
      state.detailsEdit.editingField = null;
    },
    discardFieldChanges(state, action: PayloadAction<string>) {
      const fieldName = action.payload;
      if (
        state.detailsEdit.editedDetails &&
        state.detailsEdit.originalDetails
      ) {
        // Revert only the specific field to its original value
        (state.detailsEdit.editedDetails as Record<string, unknown>)[
          fieldName
        ] =
          state.detailsEdit.originalDetails[
            fieldName as keyof EnterpriseDetail
          ];
      }
      state.detailsEdit.editingField = null;
    },
    clearEnterpriseDetails(state) {
      state.detailsEdit.originalDetails = null;
      state.detailsEdit.editedDetails = null;
      state.detailsEdit.editingField = null;
    },
  },
});

export const {
  setSearchInput,
  setStatus,
  clearFilters,
  setOriginalDetails,
  startEditingField,
  stopEditingField,
  updateEditedField,
  resetEditedDetails,
  discardChanges,
  discardFieldChanges,
  clearEnterpriseDetails,
} = enterpriseSlice.actions;

// Selectors
export const selectEnterpriseFilters = (state: RootState) =>
  state.enterprise.filters;

export const selectOriginalDetails = (state: RootState) =>
  state.enterprise.detailsEdit.originalDetails;

export const selectEditedDetails = (state: RootState) =>
  state.enterprise.detailsEdit.editedDetails;

export const selectEditingField = (state: RootState) =>
  state.enterprise.detailsEdit.editingField;

// Selector to get changed fields
export const selectChangedFields = (state: RootState) => {
  const { originalDetails, editedDetails } = state.enterprise.detailsEdit;
  if (!originalDetails || !editedDetails) return {};

  const changedFields: Record<string, { original: unknown; edited: unknown }> =
    {};
  const keys = new Set<string>([
    ...Object.keys(originalDetails),
    ...Object.keys(editedDetails),
  ]);

  keys.forEach((key) => {
    const originalValue = originalDetails[key as keyof EnterpriseDetail];
    const editedValue = editedDetails[key as keyof EnterpriseDetail];

    // Deep comparison for arrays
    if (JSON.stringify(originalValue) !== JSON.stringify(editedValue)) {
      changedFields[key] = { original: originalValue, edited: editedValue };
    }
  });

  return changedFields;
};

// Selector to check if there are any unsaved changes
export const selectHasChanges = (state: RootState) => {
  const changedFields = selectChangedFields(state);
  return Object.keys(changedFields).length > 0;
};

export default enterpriseSlice.reducer;
